import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast, useConfirm, Button, documentTitle, navLabel, useDocumentTitle } from '@region13/ui'
import type {
  FieldLayout, LayoutInput, VenueCatalogUpdate, VenueMapSummary,
  VenueDetail as VenueDetailType,
} from '@region13/shared-types'
import { Card } from '../components/ui/Card'
import { LoadFailure } from '../components/LoadFailure'
import { FieldMapsCard } from '../components/FieldMapsCard'
import { LayoutDialog } from '../components/LayoutDialog'
import { VenueDetailsCard } from '../components/VenueDetailsCard'
import { api } from '../lib/api'
import { FIELDS_NAV } from '../lib/nav'

export const Route = createFileRoute('/venues/$slug')({ component: VenueDetail })

function VenueDetail() {
  const { slug } = Route.useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const { confirm } = useConfirm()
  const [editingLayout, setEditingLayout] = useState<FieldLayout | null>(null)

  const venue = useQuery({ queryKey: ['venue', slug], queryFn: () => api.venueDetail(slug) })
  // A child page heads from its data, so the tab title waits for the venue.
  useDocumentTitle(documentTitle(FIELDS_NAV, venue.data?.title ?? null))
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['venue', slug] })
    qc.invalidateQueries({ queryKey: ['venues'] })
  }

  const updateM = useMutation({
    mutationFn: (v: { id: string; input: LayoutInput }) => api.updateLayout(v.id, v.input),
    onSuccess: () => {
      invalidate()
      setEditingLayout(null)
      toast.success('Layout saved')
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteLayout(id),
    onSuccess: () => {
      invalidate()
      toast.success('Layout deleted')
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const deleteMapM = useMutation({
    mutationFn: (variant: string) => api.deleteVariant(slug, variant),
    onSuccess: () => {
      invalidate()
      toast.success('Map deleted')
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const matchM = useMutation({
    mutationFn: (v: { id: string; uid: string | null }) => api.matchSubfield(v.id, v.uid),
    onSuccess: () => { invalidate(); toast.success('Linked') },
    onError: (e) => toast.error((e as Error).message),
  })
  const venueM = useMutation({
    mutationFn: (patch: VenueCatalogUpdate) => api.updateVenue(slug, patch),
    onSuccess: (res) => {
      invalidate()
      // Surface the alt-text side effect: a rename silently rewriting stored
      // accessibility copy is exactly the kind of thing an operator should see.
      toast.success(
        res.altsRefreshed > 0
          ? `Venue saved · ${res.altsRefreshed} map alt text${res.altsRefreshed === 1 ? '' : 's'} updated`
          : 'Venue saved',
      )
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const archiveM = useMutation({
    mutationFn: () => api.archiveVenue(slug),
    onSuccess: (res) => {
      invalidate()
      toast.success(
        `${venue.data?.title ?? 'Venue'} archived · ${res.deactivated} ${res.deactivated === 1 ? 'field' : 'fields'} taken out of scheduling`,
      )
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const restoreM = useMutation({
    mutationFn: () => api.restoreVenue(slug),
    onSuccess: (res) => {
      invalidate()
      toast.success(
        `${venue.data?.title ?? 'Venue'} restored · ${res.deactivated} ${res.deactivated === 1 ? 'field' : 'fields'} back in scheduling`,
      )
    },
    onError: (e) => toast.error((e as Error).message),
  })

  async function onArchive() {
    const ok = await confirm({
      title: `Archive "${venue.data?.title}"?`,
      body: 'Its fields are switched off, so the scheduler and the practice planner stop using them. Its maps stay on the website, and Restore puts back exactly what this switched off.',
      confirmLabel: 'Archive',
    })
    if (ok) archiveM.mutate()
  }

  async function onRestore() {
    const ok = await confirm({
      title: `Restore "${venue.data?.title}"?`,
      body: 'The fields this archive switched off come back on. Fields you deactivated yourself stay off.',
      confirmLabel: 'Restore',
    })
    if (ok) restoreM.mutate()
  }

  async function onDelete(l: FieldLayout) {
    const ok = await confirm({
      title: `Delete "${l.name}"?`,
      body: 'This also deletes its subfields.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!ok) return
    deleteM.mutate(l.id)
  }

  async function onDeleteMap(m: VenueMapSummary) {
    const ok = await confirm({
      title: `Delete the "${m.label ?? m.variant}" map?`,
      body: 'The drawn map and its image are removed, and the website stops showing that map section after the next publish. The venue\'s layouts and subfields are not affected.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!ok) return
    deleteMapM.mutate(m.variant)
  }

  if (venue.isLoading) return <p role="status" className="py-16 text-center text-sm text-ink-soft">Loading…</p>
  if (venue.isError || !venue.data) return <LoadFailure error={venue.error} noun="venue" />

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Breadcrumb" className="text-xs text-ink-soft">
        <Link to="/" activeOptions={{ exact: true }} className="hover:underline">{navLabel(FIELDS_NAV, '/')}</Link> &rsaquo;{' '}
        <Link to="/venues" activeOptions={{ exact: true }} className="hover:underline">{navLabel(FIELDS_NAV, '/venues')}</Link> &rsaquo;{' '}
        <span aria-current="page">{venue.data.title}</span>
      </nav>
      <h1 className="text-lg font-extrabold text-ink">
        {venue.data.title}
        {venue.data.catalog.retired && (
          <span className="ml-2 text-xs font-semibold text-ink-soft">(archived)</span>
        )}
      </h1>
      <VenueDetailsCard
        catalog={venue.data.catalog}
        busy={venueM.isPending || archiveM.isPending || restoreM.isPending}
        onSave={(patch) => venueM.mutate(patch)}
        onArchive={() => void onArchive()}
        onRestore={() => void onRestore()}
      />
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-brand-maroon">
          Layouts{venue.data.layouts.length > 0 && ` (${venue.data.layouts.length})`}
        </h2>
        <p className="text-xs text-ink-soft">
          A layout is what games are scheduled on: it holds the subfields and their InLeague
          matches. Open one to change or unlink those matches.
        </p>
        {venue.data.layouts.length === 0 && (
          <p className="text-sm text-ink-soft">
            Layouts are created by drawing and saving a map in the{' '}
            <a href="/map" className="underline hover:no-underline">map editor</a>, not here.
          </p>
        )}
        {venue.data.layouts.map((l) => (
          <Card key={l.id} row>
            <Link to="/layouts/$layoutId" params={{ layoutId: l.id }} className="flex min-w-0 flex-col hover:underline">
              <span className="font-semibold text-ink">
                {l.name}
                {!l.active && <span className="ml-2 text-xs font-normal text-ink-soft">(inactive)</span>}
              </span>
              <span className="text-xs uppercase tracking-wide text-ink-soft">{l.purpose}</span>
            </Link>
            <div className="flex shrink-0 gap-2">
              <Button variant="secondary" onClick={() => setEditingLayout(l)}>Edit</Button>
              <Button variant="danger" onClick={() => void onDelete(l)}>Delete</Button>
            </div>
          </Card>
        ))}
      </section>
      <FieldMapsCard maps={venue.data.maps} onDelete={(m) => void onDeleteMap(m)} />
      <ReconcileCard venue={venue.data} busy={matchM.isPending} onLink={(id, uid) => matchM.mutate({ id, uid })} />
      {editingLayout && (
        <LayoutDialog
          open
          initial={editingLayout}
          busy={updateM.isPending}
          onClose={() => setEditingLayout(null)}
          onSave={(input) => updateM.mutate({ id: editingLayout.id, input })}
        />
      )}
    </div>
  )
}

function ReconcileCard({ venue, busy, onLink }: {
  venue: VenueDetailType
  busy: boolean
  onLink: (subfieldId: string, uid: string) => void
}) {
  if (venue.unmatched_inleague_fields.length === 0) return null
  const noTargets = venue.unmatched_game_subfields.length === 0
  return (
    <Card className="flex flex-col gap-2 border-brand-gold/40">
      <h2 className="text-sm font-semibold text-brand-maroon">
        Unmatched InLeague fields ({venue.unmatched_inleague_fields.length})
      </h2>
      {noTargets && (
        <p className="text-xs text-ink-soft">No unlinked game/tournament subfields to link these to yet.</p>
      )}
      <ul className="flex flex-col gap-1">
        {venue.unmatched_inleague_fields.map((f) => (
          <li key={f.uid} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate text-ink">{f.name ?? f.uid}</span>
            <select
              className="input max-w-[12rem] shrink-0"
              aria-label={`Link ${f.name ?? f.uid} to a subfield`}
              value=""
              disabled={busy || noTargets}
              onChange={(e) => { if (e.target.value) onLink(e.target.value, f.uid) }}
            >
              <option value="">Link to subfield…</option>
              {venue.unmatched_game_subfields.map((s) => (
                <option key={s.id} value={s.id}>{s.layout_name} · {s.label}</option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </Card>
  )
}
